import React, { useContext, useEffect, useRef, useState } from "react";
import WorkflowPreview from "../pages/WorkflowPreview";
import styles from "../css/workflow.module.scss";
import { Button, Container } from "reactstrap";
import { LIBRARY_PATHS } from "../../../../Routes/paths";
import { useNavigate, useParams } from "react-router-dom";
import CommonOffcanvasLayout from "Components/Common/CommonOffcanvasLayout";
import {
  useInsertWorkflowData,
  useSelectWorkflowData,
} from "modules/Library/Workflow/api/useWorkflowQuery";
import {
  CurrentUser,
  GetUserData,
  showToastMessage,
} from "common/service/CommonService";
import { useQueryClient } from "@tanstack/react-query";
import Reason from "common/utilities/Reason/Reason";
import DeleteModal from "Components/Common/DeleteModal";
import CommonTabHeader from "Components/Common/CommonTabHeader";
import { BreadcrumbContext } from "Layouts/Header";
import { DEFAULT_STAGE } from "./WorkflowState";
import StageInputForm from "./StageInputForm";
import { EditStage, Stage, StagePayload } from "../helpers/workflow.types";
import { validations } from "validations/FormValidations";

/* =====================================================
   Component
===================================================== */

const WorkflowBuilder: React.FC = () => {
  document.title = "Workflow | ZENTIXS VISTA"
  const [stages, setStages] = useState<Stage[]>([DEFAULT_STAGE as Stage]);
  const [editStage, setEditStage] = useState<EditStage | null>(null);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [workflowName, setWorkflowName] = useState<string>("Workflow");
  const [updatedStage, setUpdatedStage] = useState<EditStage | null>(null);
  //Reason Modal
  const [isReasonWorkflowModal, setIsReasonWorkflowModal] =
    useState<boolean>(false);
  const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
  //Delete Modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [showDeleteReason, setShowDeleteReason] = useState(false);
  const [deleteStage, setDeleteStage] = useState<EditStage | null>(null);

  //toggles
  const toggleReasonWorkflowModal = (): void => {
    setIsReasonWorkflowModal((prev) => !prev);
  };
  const toggleReasonModal = (): void => {
    setIsReasonModal((prev) => !prev);
  };
  const toggleDeleteModal = (): void => setDeleteModal(!deleteModal);
  const toggleDeleteReason = (): void => setShowDeleteReason(!showDeleteReason);
  // Use Param
  const { id } = useParams<{ id: string }>();

  const workflowid = Number(id) || null;

  const { selectQuery } = useSelectWorkflowData(workflowid);
  const { data: selectedWorkflowData } = selectQuery;

  const { insertWorkflowData } = useInsertWorkflowData();
  const previewRef = useRef<any>(null);
  //queryClient
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);

  const isSaveDisabled = stages.filter((stage) => !stage.isDeleted).length < 2;

  const openAddStage = () => {
    setEditStage(null);
    setIsCanvasOpen(true);
  };

  const openEditStage = (stage: EditStage) => {
    setEditStage(stage);
    setIsCanvasOpen(true);
  };

  const closeCanvas = () => {
    setEditStage(null);
    setIsCanvasOpen(false);
  };



  /* -----------------------------
     ADD STAGE (SAFE ID)
  ----------------------------- */
  const addStage = (payload: StagePayload) => {
    setStages((prev) => {
      const maxId = Math.max(...prev.map((s) => s.stageId));

      // Set isLocked = false for all previous stages
      const updatedPrevStages = prev.map((stage) => ({
        ...stage,
        islocked: false,
      }));

      const newStage: Stage = {
        ...payload,
        id: undefined, // no DB id yet
        stageId: maxId + 1,
        islocked: true,
      };

      return [...updatedPrevStages, newStage];
    });

    setEditStage(null);
  };

  const btnReasonClick = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    setStages((prev) => {
      const updated = prev.map((s) =>
        s.stageId === updatedStage?.stageId
          ? { ...s, ...updatedStage, updatereason: reasonPayload.reason }
          : s,
      );
      // Recalculate islocked: last active stage = true, rest = false
      const activeStages = updated.filter((s) => !s.isDeleted);
      const lastActiveId = activeStages[activeStages.length - 1]?.stageId;
      return updated.map((s) => ({
        ...s,
        islocked: !s.isDeleted && s.stageId === lastActiveId,
      }));
    });
    setEditStage(null);
    toggleReasonModal();
    closeCanvas();
  };

  const deleteStageById = (stageId: number, reason?: string) => {
    if (stageId === 1) return; // protect "Not Started"

    setStages((prev) => {
      const activeStages = prev.filter((s) => !s.isDeleted);
      const targetIdx = activeStages.findIndex((s) => s.stageId === stageId);
      const targetStage = prev.find((s) => s.stageId === stageId);
      const targetDbId = targetStage?.id;

      let upperStageId: number | null = null;
      let newNextStageId: number | null = null;

      if (targetIdx > 0) {
        upperStageId = activeStages[targetIdx - 1].stageId;
        if (targetIdx + 1 < activeStages.length) {
          newNextStageId = activeStages[targetIdx + 1].stageId;
        }
      }

      return prev.map((stage) => {
        if (stage.stageId === stageId) {
          return { ...stage, isDeleted: true,islocked:false, ...(reason && { updatereason: reason }) };
        }

        let updated = { ...stage };
        let changed = false;

        if (upperStageId !== null && stage.stageId === upperStageId) {
          if (newNextStageId !== null) {
            const { id, ...nextWithoutId } = updated.transitions?.next || {};
            updated.transitions = {
              ...updated.transitions,
              next: {
                ...nextWithoutId,
                toStageId: newNextStageId,
                actionLabel: "",
                status: "",
              },
            };
          } else {
            if (updated.transitions) {
              const { next, ...rest } = updated.transitions;
              updated.transitions = rest;
            }
          }

          updated.islocked = newNextStageId !== null ? false : true;
          changed = true;
        }

        if (
          stage.returnTo != null &&
          (stage.returnTo == stageId || (targetDbId != null && stage.returnTo == targetDbId))
        ) {
          updated.returnTo = null;
          changed = true;
        }

        if (stage.transitions?.returns) {
          const filteredReturns = stage.transitions.returns.filter(
            (r) =>
              r.toStageId != stageId &&
              (targetDbId == null || r.toStageId != targetDbId)
          );
          if (filteredReturns.length !== stage.transitions.returns.length) {
            updated.transitions = {
              ...stage.transitions,
              ...(updated.transitions || {}),
              returns: filteredReturns,
            };
            changed = true;
          }
        }

        return changed ? updated : stage;
      });
    });
    setEditStage(null);
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason: string;
  }): void => {
    if (reasonPayload.id !== null) {
      deleteStageById(reasonPayload.id, reasonPayload.reason);
    }
    setDeleteModal(false);
    setShowDeleteReason(false);
  };

  /* -----------------------------
     SAVE WORKFLOW
  ----------------------------- */
  const handleSaveWorkflow = (): void => {
    if (isSaveDisabled) {
      showToastMessage("error", "Add at least one stage beyond 'Not Started' before saving.");
      return;
    }

    // ✅ VALIDATE FIRST
    const nameValidation = validations.workflow.name.validate(workflowName);
    if (nameValidation !== true) {
      showToastMessage("error", nameValidation);
      return;
    }

    const isValid = previewRef.current?.validateEdges();

    if (!isValid) return;

    if (workflowid) {
      toggleReasonWorkflowModal();
      return;
    }

    const user = GetUserData() as CurrentUser;
    const workflow = {
      
      
      workflowid: workflowid ?? null,
      name: workflowName,
      stages: stages.map((s, i) => ({
        stageId: s.id, // DB ID
        stageName: s.stageName,
        role: s.role,
        roleName: s.roleName,
        returnTo: s.returnTo, // UI stageId (backend maps)
        sequence: s.stageId, // explicit order
        showInList: s.showInList,
        optionalStage: s.optionalStage,
        transitions: {
          ...(s.transitions?.next && {
            next: s.transitions.next,
          }),
          ...(s.returnTo &&
            s.transitions?.returns?.length && {
            returns: s.transitions.returns,
          }),
        },
        isDeleted: s.isDeleted ?? false,
        updatereason: s.updatereason,
        islocked: s.islocked,
      })),
    };

    insertWorkflowData.mutate(workflow, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["workflow-selectall"] });
        showToastMessage("success", response?.message);
        navigate(LIBRARY_PATHS.WORKFLOW_LIST);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message || error);
      },
    });
  };

  const btnWorkflowReasonClick = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    const user = GetUserData() as CurrentUser;
    const workflow = {
      workflowid: reasonPayload.id ?? null,
      updatereason: reasonPayload.reason ?? null,
      name: workflowName,
      stages: stages.map((s, i) => ({
        stageId: s.id, // DB ID
        stageName: s.stageName,
        role: s.role,
        roleName: s.roleName,
        returnTo: s.returnTo, // UI stageId (backend maps)
        sequence: s.stageId, // explicit order
        showInList: s.showInList,
        optionalStage: s.optionalStage,
        transitions: {
          ...(s.transitions?.next && {
            next: s.transitions.next,
          }),

          ...(s.returnTo &&
            s.transitions?.returns?.length && {
            returns: s.transitions.returns,
          }),
        },
        isDeleted: s.isDeleted ?? false,
        updatereason: s.updatereason,
        islocked: s.islocked,
      })),
    };

    

    insertWorkflowData.mutate(workflow, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["workflow-selectall"] });
        queryClient.invalidateQueries({ queryKey: ["workflow-select"] });
        toggleReasonWorkflowModal();
        showToastMessage("success", response?.message);
        navigate(LIBRARY_PATHS.WORKFLOW_LIST);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message || error);
      },
    });
  };

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Workflow", link: LIBRARY_PATHS.WORKFLOW_LIST },
      { text: workflowid ? "Edit Workflow" : "Add Workflow" },
    ]);

    return () => {
      breadcrumbContext?.setBreadcrumbItems?.([]);
    };
  }, [workflowid]);

  useEffect(() => {
    if (!selectedWorkflowData?.data) return;

    const uiStages: Stage[] = selectedWorkflowData.data.stages.map(
      (stage: any, index: number) => ({
        ...stage,
        id: stage.stageId, // DB ID preserved
        stageId: stage.stageId, // UI order regenerated
      }),
    );

    setWorkflowName(selectedWorkflowData.data.name || "Workflow");
    setStages(uiStages);
  }, [selectedWorkflowData]);

  /* =====================================================
     Render
  ===================================================== */

  return (
    <Container fluid>
      <CommonTabHeader
        title={workflowid ? "Edit Workflow" : "Add Workflow"}
        editableTitle={workflowName}
        onTitleChange={setWorkflowName}
        validationRule={validations.workflow.name.validate}
        showAction={false}
      />

      <div className="text-end my-2">
        <Button
          size="sm"
          color="primary"
          outline
          onClick={handleSaveWorkflow}
          disabled={isSaveDisabled}
        >
          {insertWorkflowData.isPending && !workflowid ? (
            <span className="spinner-border spinner-border-sm me-1" />
          ) : null}
          Save Workflow
        </Button>
      </div>

      {/* Workflow Name */}

      <div>
        <CommonOffcanvasLayout
          isOpen={isCanvasOpen}
          toggle={closeCanvas}
          title={editStage ? "Edit Stage" : "Add Stage"}
          footer={
            <div className="d-flex justify-content-between gap-2">
              <Button size="sm" color="secondary" outline onClick={closeCanvas}>
                Cancel
              </Button>

              <Button
                size="sm"
                color="primary"
                outline
                onClick={() => {
                  document
                    .getElementById("stage-form-submit")
                    ?.dispatchEvent(
                      new Event("submit", { cancelable: true, bubbles: true }),
                    );
                }}
              >
                Save
              </Button>
            </div>
          }
        >
          <StageInputForm
            onAddStage={(payload) => {
              addStage(payload);
              closeCanvas();
            }}
            onUpdateStage={(payload) => {
              if (!workflowid) {
                setStages((prev) => {
                  const updated = prev.map((s) =>
                    s.stageId === payload.stageId ? { ...s, ...payload } : s
                  );
                  // Recalculate islocked: last active stage = true, rest = false
                  const activeStages = updated.filter((s) => !s.isDeleted);
                  const lastActiveId = activeStages[activeStages.length - 1]?.stageId;
                  return updated.map((s) => ({
                    ...s,
                    islocked: !s.isDeleted && s.stageId === lastActiveId,
                  }));
                });
                closeCanvas();
              } else {
                setUpdatedStage(payload);
                toggleReasonModal();
              }
            }}
            editStage={editStage}
            stages={stages}
          />
        </CommonOffcanvasLayout>
      </div>

      <div className={`row ${styles.workflow_builder} mt-2`}>
        <div className="col-md-12">
          <WorkflowPreview
            stages={stages}
            ref={previewRef}
            onEditStage={openEditStage}
            onDeleteStage={(stage) => {
              if (!workflowid) {
                deleteStageById(stage.stageId);
              } else {
                setDeleteStage(stage);
                toggleDeleteModal();
              }
            }}
            setStages={setStages}
            onAddStage={openAddStage} // 👈 NEW
          />
        </div>
      </div>
      {isReasonWorkflowModal && (
        <Reason
          isOpen={isReasonWorkflowModal}
          toggle={toggleReasonWorkflowModal}
          action="Update"
          id={workflowid}
          click={btnWorkflowReasonClick}
          isLoading={insertWorkflowData.isPending}
        />
      )}
      {isReasonModal && (
        <Reason
          isOpen={isReasonModal}
          toggle={toggleReasonModal}
          action="Update"
          id={updatedStage?.stageId}
          click={btnReasonClick}
          isLoading={false}
        />
      )}
      {deleteModal && (
        <DeleteModal
          isopen={deleteModal}
          toggle={toggleDeleteModal}
          recordId={deleteStage?.stageId}
          recordName={deleteStage?.stageName}
          onDeleteClick={confirmDelete}
          showDeleteReason={showDeleteReason}
          toggleDeleteReason={toggleDeleteReason}
        />
      )}
    </Container>
  );
};

export default WorkflowBuilder;
